import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Table,
  Spinner,
  Button,
  Alert,
  Badge,
  Modal,
  Form,
  ButtonGroup,
} from "react-bootstrap";
import firebase from "firebase/app";
import "firebase/firestore";
import { useAuth } from "../../contexts/AuthContext";
import { fires } from "../../firebase";
import { useNavigate } from "react-router-dom";

export default function AdminTaskManagement() {
  const TASK_STATUSES = ["pending","working","completed","cancelled","failed"];
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tasks, setTasks] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [statusModalTask, setStatusModalTask] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [rescheduleModalTask, setRescheduleModalTask] = useState(null);
  const [newScheduledAt, setNewScheduledAt] = useState("");
  const [showAllTasks, setShowAllTasks] = useState(false);

  useEffect(() => {
    checkAdmin();
  }, [currentUser]);

  useEffect(() => {
    if (isAdmin) {
      loadTasks();
    }
  }, [isAdmin]);

  const checkAdmin = async () => {
    try {
      if (!currentUser) {
        navigate("/");
        return;
      }
      const adminDoc = await fires.collection("admin").doc(currentUser.uid).get();
      if (!adminDoc.exists) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }
      const roles = adminDoc.data()?.roles || [];
      if (!roles.includes("management")) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }
      setIsAdmin(true);
    } catch (err) {
      console.error("Admin check failed:", err);
      setError("Failed to verify access");
      setLoading(false);
    }
  };

  const loadTasks = async () => {
    setLoading(true);
    setError("");
    try {
      let snapshot;
      try {
        snapshot = await fires
          .collection("tasks")
          .orderBy("scheduledAt", "asc")
          .get();
      } catch (e1) {
        if (e1.code === "failed-precondition" || e1.code === "unavailable") {
          snapshot = await fires.collection("tasks").get();
        } else throw e1;
      }

      const list = snapshot.docs.map((doc) => {
        const d = doc.data();
        const scheduledAt = d.scheduledAt?.toDate?.() ?? (d.scheduledAt ? new Date(d.scheduledAt) : null);
        const rawStatus = (d.status ?? "").toString().toLowerCase();
        const status = TASK_STATUSES.includes(rawStatus) ? rawStatus : "pending";
        return {
          id: doc.id,
          ...d,
          eventId: d.eventId ?? "",
          task: d.task ?? "",
          type: d.type ?? "",
          title: d.title ?? d.name ?? "",
          status,
          description: d.description ?? "",
          scheduledAt,
        };
      });

      if (!snapshot.query?.orderBy) {
        list.sort((a, b) => {
          const ta = a.scheduledAt ? a.scheduledAt.getTime() : Infinity;
          const tb = b.scheduledAt ? b.scheduledAt.getTime() : Infinity;
          return ta - tb;
        });
      }
      setTasks(list);
    } catch (err) {
      console.error("Error loading tasks:", err);
      setError(err?.message || "Failed to load tasks");
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "—";
    const d = date instanceof Date ? date : new Date(date);
    return isNaN(d.getTime()) ? "—" : d.toLocaleString();
  };

  const statusVariant = (status) => {
    if (status === "completed") return "success";
    if (status === "working") return "primary";
    if (status === "cancelled" || status === "failed") return "danger";
    return "warning"; // pending
  };

  const openStatusModal = (task) => {
    setStatusModalTask(task);
    setNewStatus(task.status || "pending");
  };

  const closeStatusModal = () => {
    setStatusModalTask(null);
    setNewStatus("");
  };

  const confirmUpdateStatus = async () => {
    if (!statusModalTask?.id || !newStatus) return;
    setUpdatingId(statusModalTask.id);
    setError("");
    try {
      await fires.collection("tasks").doc(statusModalTask.id).update({ status: newStatus });
      setTasks((prev) =>
        prev.map((t) => (t.id === statusModalTask.id ? { ...t, status: newStatus } : t))
      );
      closeStatusModal();
    } catch (err) {
      console.error("Error updating task status:", err);
      setError(err?.message || "Failed to update status");
    } finally {
      setUpdatingId(null);
    }
  };

  const openRescheduleModal = (task) => {
    setRescheduleModalTask(task);
    const d = task.scheduledAt instanceof Date ? task.scheduledAt : task.scheduledAt ? new Date(task.scheduledAt) : new Date();
    setNewScheduledAt(d.toISOString().slice(0, 16));
  };

  const closeRescheduleModal = () => {
    setRescheduleModalTask(null);
    setNewScheduledAt("");
  };

  const confirmReschedule = async () => {
    if (!rescheduleModalTask?.id || !newScheduledAt) return;
    setUpdatingId(rescheduleModalTask.id);
    setError("");
    try {
      const date = new Date(newScheduledAt);
      const timestamp = firebase.firestore.Timestamp.fromDate(date);
      await fires.collection("tasks").doc(rescheduleModalTask.id).update({ scheduledAt: timestamp });
      setTasks((prev) =>
        prev.map((t) => (t.id === rescheduleModalTask.id ? { ...t, scheduledAt: date } : t))
      );
      closeRescheduleModal();
    } catch (err) {
      console.error("Error rescheduling task:", err);
      setError(err?.message || "Failed to reschedule");
    } finally {
      setUpdatingId(null);
    }
  };

  if (!currentUser || loading) {
    return (
      <Container className="py-5">
        <div className="d-flex justify-content-center align-items-center">
          <Spinner animation="border" />
          <span className="ms-2">Loading...</span>
        </div>
      </Container>
    );
  }

  if (!isAdmin) {
    return (
      <Container className="py-5">
        <Alert variant="danger">Access denied. Admin only.</Alert>
        <Button variant="outline-primary" onClick={() => navigate("/")}>
          Back
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Task Management</h2>
          <p className="text-muted small mb-0">Admin-only list from collection &quot;tasks&quot;</p>
        </div>
        <div className="d-flex align-items-center gap-3">
          <Form.Check
            type="switch"
            id="show-all-tasks"
            label="Show all tasks"
            checked={showAllTasks}
            onChange={(e) => setShowAllTasks(e.target.checked)}
          />
          <Button variant="outline-primary" onClick={loadTasks} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" className="me-1" /> : null}
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-2 mb-0 text-muted">Loading tasks...</p>
            </div>
          ) : (() => {
          const displayedTasks = showAllTasks ? tasks : tasks.filter((t) => t.status !== "completed");
          return displayedTasks.length === 0 ? (
            <div className="text-center py-5 text-muted">
              {tasks.length === 0 ? "No tasks found." : "No upcoming tasks. Enable “Show all tasks” to see completed."}
            </div>
          ) : (
            <Table responsive hover size="sm" className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Event ID</th>
                  <th>Task</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Scheduled at</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayedTasks.map((t, idx) => (
                  <tr key={t.id}>
                    <td className="text-muted">{idx + 1}</td>
                    <td>
                      {t.eventId ? (
                        <a
                          href={`https://eventgo.ai/event/${t.eventId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none"
                        >
                          <code className="small">{t.eventId}</code>
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>{t.task || "—"}</td>
                    <td>{t.type || "—"}</td>
                    <td><Badge bg={statusVariant(t.status)}>{t.status || "pending"}</Badge></td>
                    <td>{formatDate(t.scheduledAt)}</td>
                    <td className="text-end">
                      <ButtonGroup size="sm">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => openStatusModal(t)}
                          disabled={updatingId === t.id}
                          title="Change status"
                        >
                          {updatingId === t.id ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <i className="fas fa-tasks" />
                          )}
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => openRescheduleModal(t)}
                          disabled={updatingId === t.id}
                          title="Reschedule"
                        >
                          <i className="fas fa-calendar-alt" />
                        </Button>
                      </ButtonGroup>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          );
        })()}
        </Card.Body>
      </Card>

      <Modal show={!!statusModalTask} onHide={closeStatusModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Change task status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {statusModalTask && (
            <>
              <p className="text-muted small mb-2">
                Event ID: <code>{statusModalTask.eventId || "—"}</code> · Task: {statusModalTask.task || "—"}
              </p>
              <Form.Group>
                <Form.Label>New status</Form.Label>
                <Form.Select value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
                  {TASK_STATUSES.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeStatusModal}>Cancel</Button>
          <Button variant="primary" onClick={confirmUpdateStatus} disabled={!newStatus || newStatus === statusModalTask?.status}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!rescheduleModalTask} onHide={closeRescheduleModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Reschedule task</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {rescheduleModalTask && (
            <>
              <p className="text-muted small mb-2">
                Event ID: <code>{rescheduleModalTask.eventId || "—"}</code> · Task: {rescheduleModalTask.task || "—"}
              </p>
              <Form.Group>
                <Form.Label>New scheduled date & time</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={newScheduledAt}
                  onChange={(e) => setNewScheduledAt(e.target.value)}
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeRescheduleModal}>Cancel</Button>
          <Button variant="primary" onClick={confirmReschedule} disabled={!newScheduledAt}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
